import { Form, Formik } from "formik";
import React from "react";
import {
  Button,
  Checkbox,
  Divider,
  Grid,
  Header,
  Segment,
  Icon,
  Image,
  Popup,
} from "semantic-ui-react";
import MyTextInput from "../../app/common/form/MyTextInput";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { updateUserProfileInDb } from "../../app/firestore/firestoreService";
import { toast } from "react-toastify";
import FormAddress from "../../app/common/form/FormAddress";
import { useMediaQuery } from "react-responsive";
import MyTextArea from "../../app/common/form/MyTextArea";
import MyCheckbox from "../../app/common/form/MyCheckbox";
import MyRadioButtons from "../../app/common/form/MyRadioButtons";
import MySelectInput from "../../app/common/form/MySelectInput";
import { openModal } from "../../app/common/modals/modalSlice";
import { deleteDocFromStorage } from "../../app/firestore/firebaseService";
import {
  getManagerInitialLoginOptions,
  getManagerInitialLoginPage,
  getManagerTransactionDefaultDays,
  managerTransactionDaysOptions,
} from "../managers/managerPreferences";

export default function ProfilePage() {
  const { currentUserProfile } = useSelector((state) => state.profile);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const profilePageMountedRef = React.useRef(true);
  const managerInitialLoginOptions = getManagerInitialLoginOptions(
    currentUserProfile
  );

  React.useEffect(() => {
    return () => {
      profilePageMountedRef.current = false;
    };
  }, []);

  const handlePhotoUpload = () => {
    dispatch(openModal({ modalType: "UserProfilePhotoUpload" }));
  };

  const handlePhotoDelete = async () => {
    try {
      if (currentUserProfile.profilePhotoUrl) {
        const docRef = `profiles/${currentUserProfile.id}/profilePhoto.jpeg`;
        await deleteDocFromStorage(docRef);
        await updateUserProfileInDb({ profilePhotoUrl: null });
        toast.success("Profile photo deleted successfully");
      }
    } catch (error) {
      toast.error("Error deleting photo: " + error.message);
    }
  };

  const currentManagerPreferences =
    currentUserProfile?.managerPreferences || {};

  let initialValues = {
    ...currentUserProfile,
    isMortgageBroker: currentUserProfile?.isMortgageBroker || false,
    notificationPreferences: {
      ...(currentUserProfile?.notificationPreferences || {}),
      suggestWeeklyDeadlineEmail:
        currentUserProfile?.notificationPreferences
          ?.suggestWeeklyDeadlineEmail !== false,
      // Fall back to the legacy top-level field so a preference set before
      // this was grouped under notificationPreferences still shows correctly.
      formReviewedNotifications:
        currentUserProfile?.notificationPreferences
          ?.formReviewedNotifications ??
        currentUserProfile?.formReviewedNotifications ??
        true,
      queueNotificationsByDefault:
        currentUserProfile?.notificationPreferences
          ?.queueNotificationsByDefault || false,
      forceQueueNotifications:
        currentUserProfile?.notificationPreferences?.forceQueueNotifications ||
        false,
    },
    managerPreferences: {
      ...currentManagerPreferences,
      initialLoginPage: getManagerInitialLoginPage(currentUserProfile),
      transactionDefaultDays: {
        upcomingClosings: getManagerTransactionDefaultDays(
          currentUserProfile,
          "upcomingClosings"
        ),
        recentClosings: getManagerTransactionDefaultDays(
          currentUserProfile,
          "recentClosings"
        ),
        newListings: getManagerTransactionDefaultDays(
          currentUserProfile,
          "newListings"
        ),
        newBuyers: getManagerTransactionDefaultDays(
          currentUserProfile,
          "newBuyers"
        ),
      },
    },
    complianceChecklistOptions: {
      includeFolders:
        currentUserProfile?.complianceChecklistOptions?.includeFolders ||
        false,
      includeArchivedDocuments:
        currentUserProfile?.complianceChecklistOptions
          ?.includeArchivedDocuments || false,
      showConditional:
        currentUserProfile?.complianceChecklistOptions?.showConditional || false,
      showOptional:
        currentUserProfile?.complianceChecklistOptions?.showOptional || false,
    },
  };

  const validationSchema = Yup.object({
    lastName: Yup.string().required("You must provide a last name"),
  });

  return (
    <div className="main-page-wrapper">
      <Grid centered stackable className="small bottom margin">
        <Grid.Column width={10}>
          <Header color="blue" as="h2">
            My Profile
          </Header>
          {/* {currentUserProfile.isAdmin && (
            <Segment>
              <h3 color="red">Admin Buttons</h3>
              <Button color="red" onClick={() => checkFontSizeGtHeight()}>
                Check Font Size GT Height
              </Button>
            </Segment>
          )} */}
          <Segment clearing>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  await updateUserProfileInDb(values);
                  if (profilePageMountedRef.current) setSubmitting(false);
                  toast.success("Profile successfully updated");
                } catch (error) {
                  toast.error(error.message);
                  if (profilePageMountedRef.current) setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, dirty, isValid, values, setFieldValue }) => (
                <Form className="ui form medium margin bottom">
                  <Header color="blue">Name & Contact Info</Header>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column mobile={16} computer={5}>
                        <MyTextInput
                          name={"firstName"}
                          placeholder={"First name"}
                          data-test="firstName"
                          label="First Name"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={5}>
                        <MyTextInput
                          name={"middleName"}
                          // placeholder={"Middle name"}
                          data-test="middleName"
                          label="Middle Name"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={5}>
                        <MyTextInput
                          name={"lastName"}
                          // placeholder={"Last name"}
                          data-test="lastName"
                          label="Last Name"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column mobile={16} computer={5}>
                        <MyTextInput
                          name={"phone"}
                          // placeholder="Phone"
                          data-test="phone"
                          label="Phone"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={10}>
                        <>
                          <h5>Email</h5>
                          <div style={{ color: "#aaa" }}>
                            {currentUserProfile.email}
                          </div>
                        </>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>

                  <Divider />
                  <Header color="blue">Brokerage Address</Header>
                  <FormAddress />
                  <br />
                  <Divider />
                  <Header color="blue">Agent/Broker Details</Header>
                  <Grid stackable>
                    <Grid.Row>
                      <Grid.Column width={5}>
                        <MyTextInput
                          name="brokerLicenseNumber"
                          label="Agent License Number"
                        />
                      </Grid.Column>
                      <Grid.Column width={5}>
                        <MyTextInput
                          name="brokerNrdsId"
                          label="Agent NRDS ID"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={5}>
                        <MyTextInput
                          name="brokerageName"
                          label="Brokerage Name"
                        />
                      </Grid.Column>
                      <Grid.Column width={5}>
                        <MyTextInput name="brokerageId" label="Brokerage ID" />
                      </Grid.Column>
                      <Grid.Column width={5}>
                        <MyTextInput
                          name="brokerageLicenseNumber"
                          label="Brokerage License Number"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={5}>
                        <MyCheckbox name="isOnTeam" label="On a Team?" />
                      </Grid.Column>
                      <Grid.Column width={10}>
                        <MyTextInput
                          name="brokerTeam.teamName"
                          label="Team Name"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={5}></Grid.Column>
                      <Grid.Column width={10}>
                        <MyTextInput
                          name="brokerTeam.teamPhone"
                          label="Team Phone #"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  {currentUserProfile?.role !== "tc" &&
                    currentUserProfile?.role !== "managerassistant" &&
                    currentUserProfile?.role !== "assistant" && (
                      <>
                        <Divider />

                        <Header color="blue">
                          Display at Top of Forms (Next to Brokerage Logo)
                        </Header>
                        <Grid stackable>
                          <Grid.Row>
                            <Grid.Column width={5}>
                              <MyCheckbox
                                name="formTopLogo.showTeamName"
                                label="Show Team Name?"
                              />
                            </Grid.Column>
                            <Grid.Column width={5}>
                              <MyCheckbox
                                name="formTopLogo.showTeamPhone"
                                label="Show Team Phone Number?"
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={5}>
                              <MyCheckbox
                                name="formTopLogo.showAgentName"
                                label="Show Agent Name (Me)?"
                              />
                            </Grid.Column>
                            <Grid.Column width={5}>
                              <MyCheckbox
                                name="formTopLogo.showAgentPhone"
                                label="Show My Phone Number?"
                              />
                            </Grid.Column>
                            <Grid.Column width={5}>
                              <MyCheckbox
                                name="formTopLogo.showAgentEmail"
                                label="Show Agent Email (Me)?"
                              />
                            </Grid.Column>
                          </Grid.Row>
                          {/* <Grid.Row> */}
                          {/* <Grid.Column width={10}>
                        <MyCheckbox
                          name="formTopLogoShowCoAgentName"
                          label="Show Co-Agent Name (if applicable)?"
                        />
                      </Grid.Column> */}
                          {/* <Grid.Column width={10}>
                        <MyCheckbox
                          name="formTopLogoShowCoAgentEmail"
                          label="Show Co-Agent Email (if applicable)?"
                        />
                      </Grid.Column> */}
                          {/* </Grid.Row> */}
                        </Grid>
                      </>
                    )}
                  <Divider />
                  <Header color="blue">MLS</Header>
                  <p>
                    If you are missing an MLS or have the wrong MLS set up,
                    email{" "}
                    <a href="mailto:support@transactioner.com">
                      support@transactioner.com
                    </a>
                    <br /> Be sure to include each MLS you are connected to and
                    your login or ID for that MLS for verification.
                  </p>
                  <Divider />
                  <Header color="blue">
                    Signing Verification Certificates
                  </Header>
                  <p>
                    This will enable you to download Signing Certificates.
                    <br />
                    It adds a Signing Verification at the top of each form.
                    <br />
                    <Icon name="warning" color="orange" circular size="small" />
                    <strong>
                      You must sign as an agent in the Prepare Signatures page
                      by dragging and dropping your signature in place{" "}
                    </strong>
                    as opposed to typing your name and date inside the form when
                    filling it out.
                  </p>
                  <MyCheckbox
                    name="hasSigningVerification"
                    label="Enable Signing Certificates"
                  />
                  {currentUserProfile?.role !== "manager" &&
                    currentUserProfile?.role !== "managerassistant" && (
                      <>
                        <Divider />
                        <Header color="blue">Features</Header>
                        <MyCheckbox
                          name="showAssistFeature"
                          label="Show Assist Feature"
                        />
                        {currentUserProfile?.isBetaTester && (
                        <MyCheckbox
                          name="showStrategyFeature"
                          label="Show Strategy Feature"
                        />
                        )}
                      </>
                    )}
                  {/* Email Notification Preferences */}
                  <Divider />
                  <Header color="blue">Email Notification Preferences</Header>
                  <p>
                    Choose which events you want to receive email notifications
                    for.
                    <Popup
                      wide
                      trigger={
                        <Icon
                          name="info circle"
                          color="blue"
                          style={{ marginLeft: "4px", cursor: "pointer" }}
                        />
                      }
                      content={
                        <>
                          <p className="bold text blue mini bottom margin">
                            Email Notifications
                          </p>
                          <p className="text-dark text-normal small bottom margin">
                            For most options, you will receive an email only if
                            the box is checked and the event was caused by
                            someone else (such as a TC, assistant, or manager).
                            <i>
                              {" "}
                              If you caused the event yourself, you will not be
                              notified.
                            </i>
                            <br />
                            <br />
                            For "Document Signed" and "Form Reviewed/Filled Out
                            Notifications," the primary agent on the transaction
                            will automatically receive email notifications.
                            Other roles will only receive them if this box is
                            checked.
                            <br />
                            <br />
                            "Form Reviewed/Filled Out Notifications" are sent
                            when a client finishes reviewing or completing a
                            form and clicks to notify you. They may have
                            included a message. This notification will also
                            appear on your main overview page when you log in.
                            <br />
                          </p>
                        </>
                      }
                      position="top center"
                    />
                  </p>
                  <Grid stackable>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <MyCheckbox
                          name="notificationPreferences.transaction_created"
                          label="Transaction Created"
                        />
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <MyCheckbox
                          name="notificationPreferences.document_signed"
                          label="Document Signed (if not the primary agent)"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={8}>
                        <MyCheckbox
                          name="notificationPreferences.transactionStatus_changed"
                          label="Transaction Status Changed"
                        />
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <MyCheckbox
                          name="notificationPreferences.formReviewedNotifications"
                          label="Form Reviewed/Filled Out Notifications"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={16}>
                        <MyCheckbox
                          name="notificationPreferences.suggestWeeklyDeadlineEmail"
                          label="On Sunday/Monday, suggest sending an email to my clients with the week's deadlines"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    {currentUserProfile?.role === "manager" && (
                      <Grid.Row className="zero top padding">
                        <Grid.Column width={16}>
                          <MyCheckbox
                            name="notificationPreferences.includeInTeamNotify"
                            label="Include me as a recipient in the Notify feature"
                          />
                        </Grid.Column>
                      </Grid.Row>
                    )}
                    {/* <Grid.Row className="zero top padding">
                      <Grid.Column width={8}>
                        <MySelectInput
                          name="notificationPreferences.closingDateReminderDays"
                          label="Closing Date Reminder"
                          options={reminderDaysOptions}
                          placeholder="Select reminder days"
                          clearable
                        />
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <MySelectInput
                          name="notificationPreferences.deadlineReminderDays"
                          label="Deadline Reminder"
                          options={reminderDaysOptions}
                          placeholder="Select reminder days"
                          clearable
                        />
                      </Grid.Column>
                    </Grid.Row> */}
                  </Grid>
                  <Divider />
                  <Header color="blue">Queue Notifications</Header>
                  <p>
                    Control how sharing and signature request emails are sent
                    for your transactions. When queued, emails are grouped in
                    Pending Notifications instead of being sent immediately.
                  </p>
                  <Grid stackable>
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Checkbox
                          label="Default all new transactions to queue notifications"
                          checked={
                            !!values.notificationPreferences
                              ?.queueNotificationsByDefault
                          }
                          disabled={
                            !!values.notificationPreferences
                              ?.forceQueueNotifications
                          }
                          onChange={(e, { checked }) =>
                            setFieldValue(
                              "notificationPreferences.queueNotificationsByDefault",
                              checked
                            )
                          }
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={16}>
                        <Checkbox
                          label="Only allow queuing notifications (transactions cannot turn off queued notifications)"
                          checked={
                            !!values.notificationPreferences
                              ?.forceQueueNotifications
                          }
                          onChange={(e, { checked }) => {
                            setFieldValue(
                              "notificationPreferences.forceQueueNotifications",
                              checked
                            );
                            if (checked) {
                              setFieldValue(
                                "notificationPreferences.queueNotificationsByDefault",
                                true
                              );
                            }
                          }}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Divider />
                  <Header color="blue">Compliance</Header>
                  <Grid stackable>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <MyCheckbox
                          name="isMortgageBroker"
                          label="User is also a Mortgage Broker"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding">
                      <Grid.Column width={16}>
                        <p className="small bottom margin text-grey">
                          Default these Compliance Checklist options to checked:
                        </p>
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <MyCheckbox
                          name="complianceChecklistOptions.includeFolders"
                          label="Include Folders"
                        />
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <MyCheckbox
                          name="complianceChecklistOptions.includeArchivedDocuments"
                          label="Include Archived Documents"
                        />
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <MyCheckbox
                          name="complianceChecklistOptions.showConditional"
                          label="Show Conditional"
                        />
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <MyCheckbox
                          name="complianceChecklistOptions.showOptional"
                          label="Show Optional"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Divider />
                  <Header color="blue">Email Signature Field</Header>
                  <Grid stackable>
                    <Grid.Row>
                      <Grid.Column width={9}>
                        <MyTextArea
                          name="emailSigField"
                          label="Email Sig Field"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Divider />
                  <Header color="blue">Profile Photo</Header>
                  <p>
                    This photo will display in the upper-right corner of Client
                    Portal pages.
                    <br />
                    <i>
                      A new photo will be applied to new transactions only.
                      <br />
                      If you update your photo, existing transactions will see
                      the new version.
                    </i>
                  </p>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column mobile={16} computer={8}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "20px",
                          }}
                        >
                          {currentUserProfile.profilePhotoUrl ? (
                            <Image
                              src={currentUserProfile.profilePhotoUrl}
                              circular
                              size="small"
                              style={{
                                width: "150px",
                                height: "150px",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "150px",
                                height: "150px",
                                borderRadius: "50%",
                                backgroundColor: "#f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "2px dashed #ccc",
                              }}
                            >
                              <Icon name="user" size="big" color="grey" />
                            </div>
                          )}
                          <div>
                            <Button
                              type="button"
                              color="blue"
                              onClick={handlePhotoUpload}
                              style={{ marginBottom: "10px", display: "block" }}
                            >
                              {currentUserProfile.profilePhotoUrl
                                ? "Change Photo"
                                : "Upload Photo"}
                            </Button>
                            {currentUserProfile.profilePhotoUrl && (
                              <Button
                                type="button"
                                color="red"
                                onClick={handlePhotoDelete}
                              >
                                Delete Photo
                              </Button>
                            )}
                          </div>
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>

                  {currentUserProfile.role === "manager" && (
                    <>
                      <Divider />
                      <Header color="blue">Manager Financials Options</Header>
                      <Grid stackable>
                        <Grid.Row>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasFranchiseFee"
                              label="Franchise Fees"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasMentorFee"
                              label="Mentor Fees"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasManagerFee"
                              label="Manager Fees"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasTransactionFee"
                              label="Transaction Fees"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasDeskFee"
                              label="Desk Fees"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasDonationFee"
                              label="Donation Fees"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={4}>
                            <MyCheckbox
                              name="brokerage.hasTeamFee"
                              label="Teams"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={10}>
                            <MyTextArea
                              name="brokerage.cdaDefaultSpecialInstructions"
                              label="Default CDA Special Instructions"
                              rows="3"
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </>
                  )}
                  {/* Calendar Preferences for TC users */}
                  {currentUserProfile?.role === "tc" && (
                    <>
                      <Divider />
                      <Header color="blue">Calendar Preferences</Header>
                      <Grid stackable>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <label
                              style={{
                                fontWeight: "bold",
                                marginBottom: "10px",
                                display: "block",
                              }}
                            >
                              Default Calendar View
                            </label>
                            <MyRadioButtons
                              name="calendarPreference"
                              options={[
                                { value: "4days", label: "4 Days" },
                                { value: "twoweeks", label: "Two Weeks" },
                              ]}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </>
                  )}
                  {/* Manager Preferences for Manager and Manager Assistant users */}
                  {(currentUserProfile?.role === "manager" ||
                    currentUserProfile?.role === "managerassistant") && (
                    <>
                      <Divider />
                      <Header color="blue">Manager Preferences</Header>
                      <Grid stackable>
                        <Grid.Row>
                          <Grid.Column width={8}>
                            <label
                              style={{
                                fontWeight: "bold",
                                marginBottom: "10px",
                                display: "block",
                              }}
                            >
                              Default Calendar View
                            </label>
                            <MyRadioButtons
                              name="calendarManager"
                              options={[
                                { value: "4days", label: "4 Days" },
                                { value: "twoweeks", label: "Two Weeks" },
                              ]}
                            />
                          </Grid.Column>
                          <Grid.Column width={8}>
                            <MySelectInput
                              name="managerPreferences.initialLoginPage"
                              label="Initial Page on Login"
                              options={managerInitialLoginOptions}
                              placeholder="Select initial page"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <p className="small bottom margin text-grey">
                              Default number of days on Transactions Page:
                            </p>
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MySelectInput
                              name="managerPreferences.transactionDefaultDays.upcomingClosings"
                              label="Upcoming Closings"
                              options={managerTransactionDaysOptions}
                              placeholder="Select days"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MySelectInput
                              name="managerPreferences.transactionDefaultDays.recentClosings"
                              label="Recent Closings"
                              options={managerTransactionDaysOptions}
                              placeholder="Select days"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MySelectInput
                              name="managerPreferences.transactionDefaultDays.newListings"
                              label="New Listings"
                              options={managerTransactionDaysOptions}
                              placeholder="Select days"
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MySelectInput
                              name="managerPreferences.transactionDefaultDays.newBuyers"
                              label="New Buyers"
                              options={managerTransactionDaysOptions}
                              placeholder="Select days"
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </>
                  )}
                  <br />
                  <br />
                  <Button
                    loading={isSubmitting}
                    disabled={!dirty || isSubmitting}
                    type="submit"
                    floated={isMobile ? "left" : "right"}
                    primary
                    content="Save"
                    className={isMobile ? "fluid large" : "large"}
                  />
                </Form>
              )}
            </Formik>
          </Segment>
        </Grid.Column>
      </Grid>
    </div>
  );
}
